import CharacterDescriptionInputs from '../CharacterDescription/CharacterDescriptionInputs';
import { handleSubmitStepTwo, saveCharacterForImagePrompt } from '../../services/builderHelpers';
import CharacterLibrary from '../CharacterLibrary';
import { setCurrentStoryStep } from '../../../../helper/helper';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import TrainingPopup from '../../../trainingvideos/TrainingPopup';
import { CONFIG } from '../../../../config/mainConfig';
import { TeacherOnboardingStatusEnum } from '../../../../interfaces/user.interface';
import Button from '../../../../components/Common/Button';
import { Toast } from '../../../../components/Common/Toast';
import { BookModel, MidjourneyModeGeneration } from '../../../../interfaces/book.interface';
import {
  CharacterModel,
  StoryCharacterAgeUnitEnum,
  StoryCharacterEnum,
} from '../../../../interfaces/character.interface';
import { IStoryData, setStoryData } from '../../../../store/storySlice';
import {
  SelectedMainCharacterPopupEnum,
  setCurrentStep,
  setEnsureToUseSelectedMainCharacterPopup,
  setUploadImageExamplePopup,
} from '../../../../store/appSlice';
import { AppDispatch, RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { createCharacterValidationSchema } from '../../configs/step-character-builder-sheme';
import { StepCharacterBuilderProps } from '../../models/StepCharacterBuilderProps.interface';
import { updateUser } from '../../services/character.service';
import CharacterLoading from './components/CharacterLoading';
import CharacterIllustrationHeader from './components/CharacterIllustrationHeader';
import CharacterPortrait from './components/CharacterPortrait';
import ImagePreview from './components/ImagePreview';
import setCharacterOutline from './services/useCharacterOutline';
import handleStoryDataUpdate from './services/handleStoryDataUpdate';
import handlePortraitUpdate from './services/handlePortraitUpdate';
import { validateCharacter } from './services/validateCharacter';
import { updateCharacterOutline } from './services/updateCharacterOutline';
import CharacterMessage from './components/CharacterMessage';
import ActionButtons from './components/ActionButtons';
import Modals from './components/BuilderModals';
import ImageUploadSection from './components/ImageUploadSection';
import Modal from '../../../../components/Common/modal/Modal';
import BackButton from '../../../../components/Common/BackButton';

const StepCharacterBuilder = ({
  setStateData,
  setLibrary,
  setStartInterval,
  setImageUploading,
  setCharacterDescription,
  setLoaders,
  setCharacterOutlineByUser,
  setSelectedImage,
  setUploadedImageUrl,
  setCurrentPortraitList,
  setCurrentPortrait,
  setCustomCurrentPortrait,
  setPreviewImage,
  setShowCharacterLibrary,
  showCharacterLibrary,
  library,
  forceUpdateLibrary,
  loaders,
  currentStep,
  imageUploading,
  draftId,
  selectedImage,
  currentPortrait,
  showFileInput,
  characterGenerationProgress,
  setCharacterGenerationProgress,
  uploadedImageUrl,
  currentPortraitList,
  previewImage,
  characterOutlineByUser,
  bookId,
  artStyle,
  stateData,
  getCharacterLibary,
  resetCharacter,
  selectedArtStyle,
  onSaveCharacterDraft,
}: StepCharacterBuilderProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const trainingVideos = useSelector((state: RootState) => state.user.trainingVideos);
  const storyData: IStoryData = useSelector((state: RootState) => state.story.storyData);
  const storyNewCharacter = useSelector((state: RootState) => state.story.storyNewCharacter);
  const uploadImageExamplePopup = useSelector((state: RootState) => state.app.uploadImageExamplePopup);
  const ensureToUseSelectedMainCharacterPopup = useSelector(
    (state: RootState) => state.app.ensureToUseSelectedMainCharacterPopup,
  );
  const [continueWithoutImage, setContinueWithoutImage] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [disabledGenerateNewCharacter, setDisabledGenerateNewCharacter] = useState<boolean>(true);
  const [shouldCoverPortrait, setShouldCoverPortrait] = useState<boolean>(false);
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const [showRelaxModeModal, setShowRelaxModeModal] = useState<boolean>(false);
  const [showPopUp, setshowPopUp] = useState<boolean>(true);
  const [libraryItems, setLibraryItems] = useState<any[] | null>(null);
  const [characterDirty, setCharacterDirty] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState<(() => void) | null>(null);
  const dispatch: AppDispatch = useDispatch();
  const bookData = useSelector((state: RootState) => state.app.bookData);

  const hasGeneratedCharacter = !!(
    bookData?.characterPortrait &&
    bookData.characterPortrait !== CONFIG.CHARACTER.DUMMY_IMAGE
  );

  const savePendingRef = useRef(false);

  // Snapshot of `characterOutlineByUser` at the moment we last fired
  // generation. Used by the moderation loop guard: re-firing the EXACT
  // same prompt that was just moderated is guaranteed to fail again, so
  // we disable the button until the user edits at least one field.
  // Transient/unknown errors do NOT trip this guard — retrying the same
  // outline is exactly what the user wants for a timeout/rate-limit/etc.
  const lastSubmittedOutlineRef = useRef<string | null>(null);
  const isModerationError = !!bookData?.characterContentFilter?.flagged;
  const submittedOutlineUnchanged =
    isModerationError &&
    lastSubmittedOutlineRef.current !== null &&
    JSON.stringify(characterOutlineByUser) === lastSubmittedOutlineRef.current;

  // Blur autosave fires for every field change regardless of whether a
  // portrait already exists. The form values are always the source of
  // truth; once a portrait exists, the dirty flag below additionally
  // surfaces an "unsaved changes" warning so the user knows the form
  // and the rendered portrait are out of sync until they regenerate.
  useEffect(() => {
    if (savePendingRef.current) {
      savePendingRef.current = false;
      if (onSaveCharacterDraft) {
        onSaveCharacterDraft(characterOutlineByUser);
      }
    }
  }, [characterOutlineByUser, onSaveCharacterDraft]);

  const handleBlurSave = useCallback(() => {
    if (onSaveCharacterDraft) {
      savePendingRef.current = true;
    }
  }, [onSaveCharacterDraft]);

  // Dirty flag only matters when a portrait already exists — it gates the
  // "unsaved changes — regenerate to apply" banner and the leave-warning
  // modal, both of which are about the form/portrait mismatch.
  const handleFieldDirty = useCallback(() => {
    if (hasGeneratedCharacter) {
      setCharacterDirty(true);
    }
  }, [hasGeneratedCharacter]);

  const guardLeaveWithDirtyCheck = useCallback(
    (action: () => void) => {
      // Persist the current outline before navigating away regardless of
      // portrait state. Covers the case where the user edits a textarea
      // and clicks back without blurring — the blur-autosave wouldn't
      // have fired, so without this the next return to the screen would
      // render stale bookData values.
      if (onSaveCharacterDraft) {
        onSaveCharacterDraft(characterOutlineByUser);
      }
      if (characterDirty) {
        setPendingLeaveAction(() => action);
        setShowUnsavedChangesModal(true);
      } else {
        action();
      }
    },
    [characterDirty, onSaveCharacterDraft, characterOutlineByUser],
  );

  const isOnTour = user?.teacherOnboardingStatus === TeacherOnboardingStatusEnum.BookOnboarding;

  useEffect(() => {
    setCharacterOutline({
      characterOutlineByUser,
      setCharacterOutlineByUser,
    });

    return () => {
      setCustomCurrentPortrait(null);
    };
  }, []);
  const onTourCharDefaultsApplied = useRef(false);

  useEffect(() => {
    if (isOnTour && !onTourCharDefaultsApplied.current && !hasGeneratedCharacter) {
      onTourCharDefaultsApplied.current = true;
      const defaults = {
        ...characterOutlineByUser,
        characterType: StoryCharacterEnum.human,
        age: '9',
        ageUnit: StoryCharacterAgeUnitEnum.year,
        gender: 'boy',
        mics: 'Curly dark hair, brown eyes, wearing a blue t-shirt with a shark on it, and blue shorts.',
      };
      setCharacterOutlineByUser(defaults);
      dispatch(setStoryData({ ...storyData, ...defaults }));
      if (!selectedImage && (!currentPortrait || currentPortrait === CONFIG.CHARACTER.DUMMY_IMAGE)) {
        setTimeout(() => {
          setSelectedImage('./Image holder Icon.png');
          setContinueWithoutImage(true);
        }, 150);
        // Automatically click the "Continue Without Uploading Photo" button logic
        setTimeout(() => {
          const btn = document.getElementById('step-next-button');
          if (btn) btn.scrollIntoView({ behavior: 'smooth' });
        }, 250);
      }
    }
  }, [isOnTour, hasGeneratedCharacter]);

  useEffect(() => {
    if (
      ensureToUseSelectedMainCharacterPopup?.current === SelectedMainCharacterPopupEnum.SUBMIT &&
      ensureToUseSelectedMainCharacterPopup?.previous === SelectedMainCharacterPopupEnum.NEW
    ) {
      dispatch(setEnsureToUseSelectedMainCharacterPopup(null));
      startStepFour().then().catch();
    }
    if (
      isOnTour &&
      [SelectedMainCharacterPopupEnum.NEW, SelectedMainCharacterPopupEnum.EXIST].includes(
        ensureToUseSelectedMainCharacterPopup?.current!,
      )
    ) {
      dispatch(setEnsureToUseSelectedMainCharacterPopup(SelectedMainCharacterPopupEnum.SUBMIT));
    }
  }, [ensureToUseSelectedMainCharacterPopup]);

  useEffect(() => {
    handleStoryDataUpdate({
      dispatch,
      storyData,
      bookData,
      loadCharacterOutline,
    });
  }, [bookData]);

  useEffect(() => {
    handlePortraitUpdate({
      storyData,
      bookData,
      library,
      setCurrentPortrait,
      setCurrentPortraitList,
    });
  }, [storyData]);

  useEffect(() => {
    const isValid = validateCharacter(characterOutlineByUser);
    setDisabledGenerateNewCharacter(!isValid);
  }, [characterOutlineByUser]);

  useEffect(() => {
    // Skip during tour — tour defaults useEffect handles characterOutline,
    // and updateCharacterOutline would wipe age/gender for the placeholder image
    if (isOnTour) return;
    const updatedCharacterOutline = updateCharacterOutline({
      selectedImage,
      storyData,
      characterOutlineByUser,
    });
    setCharacterOutlineByUser(updatedCharacterOutline);
    if (selectedImage && selectedImage !== CONFIG.CHARACTER.DUMMY_IMAGE) {
      setContinueWithoutImage(false);
    }
  }, [selectedImage]);

  const handleGenerateNewCharacter = () => {
    if (
      (+(storyData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE &&
        +(bookData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE) ||
      bookData?.modeCharacterGeneration === MidjourneyModeGeneration.relaxed
    ) {
      const creditsToDeduct =
        bookData?.modeCharacterGeneration === MidjourneyModeGeneration.relaxed
          ? 0
          : CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;
      dispatch(
        setStoryData({
          ...storyData,
          creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) - creditsToDeduct,
        }),
      );

      // Reset progress bar to initial value so it doesn't flash at 100% from a previous generation
      setCharacterGenerationProgress(10);

      if (continueWithoutImage) {
        try {
          runHandleSubmitStepTwo();
        } catch (validationErrors: any) {
          const newErrors: any = {};
          validationErrors.inner.forEach((error: any) => {
            newErrors[error.path] = error.message;
          });
          setErrors(newErrors);
        }
      } else {
        runHandleSubmitStepTwo();
      }
    } else {
      setShowRelaxModeModal(true);
    }
  };

  const runHandleSubmitStepTwo = (): void => {
    setCharacterDirty(false);
    // Snapshot the outline we're about to submit so the loop guard can
    // detect "user clicked retry without changing anything" on the next
    // failure and block the redundant click.
    lastSubmittedOutlineRef.current = JSON.stringify(characterOutlineByUser);
    // Persist the exact outline we're submitting to bookData via the
    // existing draft-save endpoint, so when the listener fires on the
    // result (success OR error) and the rehydration cascade reads from
    // bookData, the form sees its own values back instead of a stale
    // previous-success outline.
    if (onSaveCharacterDraft) {
      onSaveCharacterDraft(characterOutlineByUser);
    }
    handleSubmitStepTwo(
      bookId,
      selectedImage,
      selectedArtStyle,
      characterOutlineByUser,
      setLoaders,
      loaders,
      setCharacterDescription,
      setUploadedImageUrl,
      setImageUploading,
      setStartInterval,
      setCharacterOutlineByUser,
      setCurrentPortrait,
      setCurrentPortraitList,
      continueWithoutImage,
      storyData,
      dispatch,
    );
  };

  const handleChangingRequest = () => {
    if (
      user?.plan?.name === CONFIG.PROMO_PLAN &&
      +(storyData?.creditsPerBookLimit || 0) <= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE &&
      +(bookData?.creditsPerBookLimit || 0) <= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE
    ) {
      setShowPlanModal(true);
    } else {
      if (
        (+(storyData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE &&
          +(bookData?.creditsPerBookLimit || 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE) ||
        bookData?.modeIllustrationGeneration === MidjourneyModeGeneration.relaxed
      ) {
        const creditsToDeduct =
          bookData?.modeIllustrationGeneration === MidjourneyModeGeneration.relaxed
            ? 0
            : CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;
        dispatch(
          setStoryData({
            ...storyData,
            creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) - creditsToDeduct,
          }),
        );
        runHandleSubmitStepTwo();
      } else {
        Toast('Additional Image Credits Needed', true);
      }
    }
  };

  const CharacterSchema = createCharacterValidationSchema(characterOutlineByUser, continueWithoutImage);
  const validateField = async (name: string, value: string) => {
    try {
      const schema = Yup.reach(CharacterSchema, name) as Yup.StringSchema | Yup.NumberSchema | Yup.BooleanSchema;
      if (schema && typeof schema.validate === 'function') {
        await schema.validate(value);
        setErrors({ ...errors, [name]: '' });
      }
    } catch (error: any) {
      setErrors({ ...errors, [name]: error.message });
    }
  };

  const doStartStepFour = async () => {
    if (storyData?.illustrationCharacter) {
      if (storyData?.illustrationCharacter?.isPortraitDeleted === false || storyData.regeneratedNewCharacter === true) {
        await saveCharacterForImagePrompt(
          storyData?.illustrationCharacter?.bookId,
          currentPortrait,
          storyData?.illustrationCharacter?.pageIndex,
          undefined,
          characterOutlineByUser,
          storyData?.uploadedImage!,
        );
      }
    }
    setCurrentStoryStep(CONFIG.STORY_STEPS.ILLUSTRATION_CREATE);
    forceUpdateLibrary();
    dispatch(setCurrentStep(3));
    dispatch(setStoryData({ ...storyData, characterBuilding: false, newRefImageUploaded: false }));
  };

  const startStepFour = async () => {
    guardLeaveWithDirtyCheck(() => {
      doStartStepFour();
    });
  };

  const handleImageChange = async (input: React.ChangeEvent<HTMLInputElement> | File) => {
    let file: File | null = null;
    if ('target' in input) {
      file = input.target.files?.[0] || null;
    } else {
      file = input;
    }
    if (!file) {
      return;
    }
    setSelectedImage(file);
    setUploadedImageUrl(null);
    setCurrentPortraitList([]);
    setCurrentPortrait(null);
    dispatch(setStoryData({ ...storyData, isDummyPic: false }));
    setContinueWithoutImage(false);
    dispatch(
      setStoryData({
        ...storyData,
        illustrationCharacter: {
          ...storyData.illustrationCharacter,
          isPortraitDeleted: false,
          newUploaded: true,
          pageData: { customCharacterId: null },
        },
      }),
    );
    getCharactersMatchingLibrary(null);
    setCurrentPortraitList([]);
  };

  useEffect(() => {
    if (library) {
      getCharacterMatchingLibrary();
    }
  }, [library]);

  const getCharacterMatchingLibrary = () => {
    setLibraryItems(
      library?.filter((character: CharacterModel) => {
        return character.artStyle === selectedArtStyle?.id;
      }),
    );
  };

  const getCharactersMatchingLibrary = (characterId?: string | null) => {
    if (characterId) {
      return library
        ?.filter(
          (character: CharacterModel) =>
            character.artStyle === selectedArtStyle?.id && character.characterId === characterId,
        )
        ?.map((character: CharacterModel) => character?.characterUrl);
    }
    return currentPortraitList;
  };

  const handleDeleteImage = async () => {
    setSelectedImage(CONFIG.CHARACTER.DUMMY_IMAGE);
    setUploadedImageUrl(CONFIG.CHARACTER.DUMMY_IMAGE);
    setCurrentPortrait(CONFIG.CHARACTER.DUMMY_IMAGE);
    setCustomCurrentPortrait(null);
    setPreviewImage(null);
    dispatch(
      setStoryData({
        ...storyData,
        isDummyPic: true,
        uploadedImage: null,
        age: String(1),
        ageUnit: StoryCharacterAgeUnitEnum.year,
        gender: '',
        animalType: undefined,
        characterType: StoryCharacterEnum.human,
        mics: '',
      }),
    );
    setContinueWithoutImage(true);
    dispatch(
      setStoryData({
        ...storyData,
        illustrationCharacter: {
          ...storyData.illustrationCharacter,
          isPortraitDeleted: true,
          pageData: {
            customCharacterId: null,
          },
        },
      }),
    );
    getCharactersMatchingLibrary(null);
    setCurrentPortraitList([]);
  };

  const loadCharacterOutline = (outliner: { [key: string]: any }) => {
    if (!outliner || Object.keys(outliner).length === 0) return;
    // Dispatch once with all keys merged. The previous per-key loop
    // captured the same stale `storyData` in every iteration, so each
    // dispatch overwrote the previous one and only the last key survived
    // — which silently broke selector reinit (characterType falling back
    // to 'human') for users returning to the form before generating.
    dispatch(setStoryData({ ...storyData, ...outliner }));
  };

  const onClose = async () => {
    await updateUser(user, 'screen2');
    setshowPopUp(false);
  };

  return (
    <>
      <Modals
        showPlanModal={showPlanModal}
        showRelaxModeModal={showRelaxModeModal}
        setShowRelaxModeModal={(value) => setShowRelaxModeModal(value)}
        startStepFour={startStepFour}
      />
      {(!showCharacterLibrary && storyNewCharacter) ||
        !((libraryItems?.length || 0) > 0) ||
        (storyData?.characterBuilding === true && storyData.showLibrary === false) ||
        (currentPortrait && currentPortrait !== CONFIG.CHARACTER.DUMMY_IMAGE && storyData.showLibrary === false) ? (
        <div className="animate__animated animate__slideInRight p-4">
          {showPopUp && !user?.isTeacherMode && user?.showTrainingPopup && user?.showTrainingPopup['screen2'] === true && (
            <TrainingPopup
              videoUrl={currentStep === 2 ? trainingVideos?.STORY_CHARCTER_BUILDER_VIDEO : null}
              onClose={onClose}
              showScreenName={'screen2'}
            />
          )}
          <CharacterIllustrationHeader
            user={user!}
            storyData={storyData}
            loaders={loaders}
            setLoaders={setLoaders}
            setLibrary={setLibrary}
            characterMatchingLibrary={libraryItems || []}
            setShowCharacterLibrary={setShowCharacterLibrary}
            getCharacterLibary={getCharacterLibary}
            setCurrentStoryStep={setCurrentStoryStep}
            currentStep={currentStep}
            imageUploading={imageUploading}
            draftId={draftId}
            onBack={guardLeaveWithDirtyCheck}
            isOnTour={isOnTour}
          />
          <CharacterMessage
            selectedImage={selectedImage}
            currentPortrait={currentPortrait}
            loaders={loaders}
            bookData={bookData!}
          />
          {showFileInput &&
            currentStep === 2 &&
            (loaders?.characterGenerating || loaders?.characterRegenerate || bookData?.isImageProcessing ? (
              <CharacterLoading characterGenerationProgress={characterGenerationProgress} />
            ) : (
              <div className=" w-full  flex flex-col gap-2 ">
                <div className=" w-full flex flex-col justify-around lg:flex-row gap-4 mt-2 rounded-[1.4rem]">
                  {!selectedImage && (!currentPortrait || currentPortrait === CONFIG.CHARACTER.DUMMY_IMAGE) && (
                    <ImageUploadSection
                      handleImageChange={handleImageChange}
                      setPreviewImage={setPreviewImage}
                      setShouldCoverPortrait={setShouldCoverPortrait}
                      setSelectedImage={setSelectedImage}
                      setContinueWithoutImage={setContinueWithoutImage}
                    />
                  )}
                  {(selectedImage || (currentPortrait && currentPortrait !== CONFIG.CHARACTER.DUMMY_IMAGE)) && (
                    <>
                      <div className="w-full h-full rounded-md lg:w-[46%] flex flex-col">
                        {(uploadedImageUrl || continueWithoutImage || currentPortrait) &&
                          !imageUploading &&
                          !loaders.characterGenerating &&
                          !loaders.characterRegenerate &&
                          (currentPortraitList?.length > 0 || currentPortrait) ? (
                          <CharacterPortrait
                            currentPortrait={currentPortrait}
                            storyData={storyData}
                            shouldCoverPortrait={shouldCoverPortrait}
                            currentPortraitList={currentPortraitList}
                            handleDeleteImage={handleDeleteImage}
                            getCharactersMatchingLibrary={getCharactersMatchingLibrary}
                            setCurrentPortrait={setCustomCurrentPortrait}
                          />
                        ) : (
                          <ImagePreview
                            previewImage={previewImage}
                            storyData={storyData}
                            handleDeleteImage={handleDeleteImage}
                          />
                        )}
                        {/*
                          Moderation-only inline banner. Sits directly under
                          the portrait so the user can see the rejected image
                          area and the explanation together. Unknown/transient
                          failures surface as toasts only (see `getLatestError`
                          and `handleSubmitStepTwo`); they do not render here
                          because the user can just retry the same prompt.
                          Moderation is the one case where the user MUST edit
                          before retrying, so it gets the fixed block.
                        */}
                        {isModerationError && (
                          <div className="mt-3 w-full">
                            <div className="rounded-lg border-l-4 border-storyerror bg-red-50 text-storyerror p-4 text-sm">
                              <p className="font-semibold mb-1">We couldn't generate this character</p>
                              <p>{bookData?.characterContentFilter?.guidance}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="w-full lg:w-[46%]">
                        <CharacterDescriptionInputs
                          setCharacterOutlineByUser={setCharacterOutlineByUser}
                          characterOutlineByUser={characterOutlineByUser}
                          handleImageChange={handleImageChange}
                          setPreviewImage={setPreviewImage}
                          previewImage={previewImage}
                          selectedImage={selectedImage}
                          continueWithoutImage={continueWithoutImage}
                          errors={errors}
                          setErrors={setErrors}
                          validateField={validateField}
                          handleDeleteImage={handleDeleteImage}
                          currentPortrait={currentPortrait}
                          setCurrentPortrait={setCurrentPortrait}
                          setShouldCoverPortrait={setShouldCoverPortrait}
                          onFieldDirty={handleFieldDirty}
                          onBlurSave={handleBlurSave}
                        />
                        {characterDirty && (
                          <div className="w-[20rem] md:w-[25rem] mx-auto pr-4 mt-1">
                            <p className="text-amber-600 text-xs font-semibold italic flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                              Unsaved changes — regenerate the image to apply them
                            </p>
                          </div>
                        )}
                        <div className="flex md:justify-end gap-4 flex-wrap my-2 w-[20rem] md:w-[25rem] mx-auto pr-4">
                          {!imageUploading &&
                            !loaders.characterGenerating &&
                            !loaders.characterRegenerate &&
                            (selectedImage || continueWithoutImage) &&
                            currentPortraitList?.length === 0 &&
                            !bookData?.characterPortrait &&
                            (!currentPortrait || currentPortrait === CONFIG.CHARACTER.DUMMY_IMAGE) && (
                              <Button
                                id="step-generate-button"
                                title={
                                  isModerationError && submittedOutlineUnchanged
                                    ? 'Edit the description to try again'
                                    : isModerationError
                                    ? 'Try a different description'
                                    : 'Generate New Character'
                                }
                                onClick={async () => handleGenerateNewCharacter()}
                                disabled={disabledGenerateNewCharacter || submittedOutlineUnchanged}
                                className={`${(disabledGenerateNewCharacter || submittedOutlineUnchanged) && 'opacity-50'
                                  } submit-button bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-sm mb-2`}
                              />
                            )}
                          {(selectedImage || continueWithoutImage || currentPortrait) &&
                            !imageUploading &&
                            !loaders.characterGenerating &&
                            !loaders.characterRegenerate &&
                            ((bookData?.currentCharacterPortraitList?.length || 0) > 0 ||
                              bookData?.characterPortrait ||
                              (currentPortrait && currentPortrait !== CONFIG.CHARACTER.DUMMY_IMAGE)) && (
                              <ActionButtons
                                limit={bookData?.characterRegenerateLimit}
                                maxLimit={CONFIG.BOOK.CHARACTER_REGENERATE_LIMIT}
                                disabledGenerateNewCharacter={
                                  disabledGenerateNewCharacter || submittedOutlineUnchanged
                                }
                                disabledContinue={
                                  currentPortrait === CONFIG.CHARACTER.DUMMY_IMAGE ||
                                  currentPortrait?.includes('base64')
                                }
                                handleChangingRequest={handleChangingRequest}
                                startStepFour={() => {
                                  if (!storyData?.illustrationCharacter?.pageIndex) {
                                    dispatch(
                                      setEnsureToUseSelectedMainCharacterPopup(SelectedMainCharacterPopupEnum.NEW),
                                    );
                                  } else {
                                    startStepFour();
                                  }
                                }}
                              />
                            )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      ) : (
        library &&
        library.filter((character: BookModel) => character.artStyle === selectedArtStyle?.id).length > 0 && (
          <CharacterLibrary
            getCharacterLibary={getCharacterLibary}
            loaders={loaders}
            library={library}
            setLibrary={setLibrary}
            setUploadedImageUrl={setUploadedImageUrl}
            setShowCharacterLibrary={setShowCharacterLibrary}
            currentPortrait={currentPortrait}
            setCurrentPortrait={setCurrentPortrait}
            currentStep={currentStep}
            setLoaders={setLoaders}
            setStateData={setStateData}
            stateData={stateData}
            artStyle={artStyle}
            startStepFour={startStepFour}
            resetCharacter={resetCharacter}
            selectedArtStyle={selectedArtStyle}
          />
        )
      )}

      {uploadImageExamplePopup === 'open' && (
        <Modal
          title={`Step Back for Best Result`}
          description={`To generate the most accurate character image, the character should be standing slightly back from the camera. Avoid close-up selfies.`}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[450px] secondary-small-popup'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={false}
        >
          <div className="flex flex-row items-center justify-center gap-6 mt-6">
            <div className={`flex flex-col w-1/2`}>
              <img src="/good_upload_image.png" alt="Good example" />
            </div>
            <div className={`flex flex-col w-1/2`}>
              <img src="/bad_upload_image.png" alt="Bad example" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-8">
            <div>
              <BackButton onClick={() => dispatch(setUploadImageExamplePopup(null))}></BackButton>
            </div>
            <button
              className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-base`}
              onClick={() => {
                dispatch(setUploadImageExamplePopup('submit'));
              }}
            >
              Continue
            </button>
          </div>
        </Modal>
      )}

      {[SelectedMainCharacterPopupEnum.NEW, SelectedMainCharacterPopupEnum.EXIST].includes(
        ensureToUseSelectedMainCharacterPopup?.current!,
      ) && (
          <Modal
            title={`Ready start your story?`}
            className={'global-absolute-popup-style font-sofiaSans secondary-small'}
            mainContainerClassName={'sm:max-w-[450px] secondary-small-popup'}
            descriptionClassContainer={'md:w-full'}
            descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
            showSubmitButton={false}
            showCloseButton={false}
            showTopRightCloseButton={true}
            showCloseButtonFunction={() => dispatch(setEnsureToUseSelectedMainCharacterPopup(null))}
          >
            <div className="flex flex-col items-center justify-center gap-6">
              <p>
                Your story is about to come to life! After this, your{' '}
                <span className="font-bold">character can’t be changed!</span>
              </p>
              <p>Please make sure you’re happy with how they look before continuing.</p>
            </div>
            <div className="flex items-center justify-end mt-8">
              <button
                className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-base`}
                onClick={() => {
                  dispatch(setEnsureToUseSelectedMainCharacterPopup(SelectedMainCharacterPopupEnum.SUBMIT));
                }}
              >
                Yes, Start!
              </button>
            </div>
          </Modal>
        )}

      {showUnsavedChangesModal && (
        <Modal
          title="Unsaved Character Changes"
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[450px] secondary-small-popup'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => {
            setShowUnsavedChangesModal(false);
            setPendingLeaveAction(null);
          }}
        >
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="text-center">
              You have changed the character details but haven't regenerated the image.
            </p>
            <p className="text-center font-semibold">
              If you leave without regenerating, your changes won't be applied to the character.
            </p>
          </div>
          <div className="flex items-center justify-between mt-8 gap-3">
            <button
              className="px-5 py-2 h-9 border border-gray-300 text-gray-700 font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm"
              onClick={() => {
                setShowUnsavedChangesModal(false);
                setCharacterDirty(false);
                if (pendingLeaveAction) {
                  pendingLeaveAction();
                  setPendingLeaveAction(null);
                }
              }}
            >
              Leave Anyway
            </button>
            <button
              className="px-5 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm"
              onClick={() => {
                setShowUnsavedChangesModal(false);
                setPendingLeaveAction(null);
              }}
            >
              Stay & Regenerate
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default StepCharacterBuilder;
